import { Button, Card, Divider, Flex, Tag, Tooltip, Typography } from 'antd'
import {
  CalendarDays,
  Mail,
  Pencil,
  Phone,
  ShieldCheck,
  UserRound
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { OrganizationRole, UserRetrieve } from 'src/api/main'
import { AvatarWithInitials, DateTimeCell, SourceTag } from 'src/components'
import { UploadAvatar } from 'src/features/profile/components'
import { UsersRoleTag, UsersStatusPill } from 'src/features/users/components'
import type { UserStatus, WorkspaceUser } from 'src/features/users/types'
import { composeDisplayName } from 'src/features/users/utils'
import { useHasWorkspacePermissions } from 'src/hooks'
import { formatPhoneNumber, WorkspacePermissionCode } from 'src/utils'

const { Text, Title } = Typography

type Props = {
  profile: UserRetrieve
  record?: WorkspaceUser
  status?: UserStatus
  photoSrc?: string
  onEdit?: () => void
}

/**
 * Entity-naming hero for the Account page — the single page-level title for the
 * whole `/account` section, replacing the per-sub-page `<Title>` blocks. Mirrors
 * `UserDetailHero` so the account surface matches the app's other detail pages.
 */
export const AccountHero = ({
  profile,
  record,
  status,
  photoSrc,
  onEdit
}: Props) => {
  const { t } = useTranslation()
  const canUploadAvatar = useHasWorkspacePermissions(
    WorkspacePermissionCode.ATTACHMENTS_UPLOAD
  )
  const user = record?.user
  const displayName = composeDisplayName(
    user?.first_name ?? profile.first_name,
    user?.last_name ?? profile.last_name,
    user?.email ?? profile.email
  )
  const email = user?.email ?? profile.email
  const formattedPhone = formatPhoneNumber(user?.phone ?? profile.phone)
  const managerName = record?.head_user
    ? composeDisplayName(
        record.head_user.first_name,
        record.head_user.last_name,
        record.head_user.email
      )
    : null
  const mfaEnabled = record?.mfa_enabled

  // Heading row: status / role / 2FA / SSO, joined by vertical dividers.
  const headingMetaItems: ReactNode[] = [
    record && status ? (
      <Tooltip key="status" title={t('users.hero.statusHint')}>
        <span>
          <UsersStatusPill status={status} inviteState={record.invite_state} />
        </span>
      </Tooltip>
    ) : null,
    record ? (
      <Tooltip key="role" title={t('users.hero.roleHint')}>
        <span>
          <UsersRoleTag role={record.role as OrganizationRole} />
        </span>
      </Tooltip>
    ) : null,
    mfaEnabled !== undefined && mfaEnabled !== null ? (
      <Tooltip key="mfa" title={t('users.hero.mfaHint')}>
        <Tag
          icon={<ShieldCheck size={11} style={{ marginInlineEnd: 4 }} />}
          color={mfaEnabled ? 'success' : 'default'}
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            fontSize: 11,
            lineHeight: 1.35,
            marginInlineEnd: 0
          }}
        >
          {mfaEnabled ? t('users.hero.mfaOn') : t('users.hero.mfaOff')}
        </Tag>
      </Tooltip>
    ) : null,
    <Tooltip key="via" title={t('users.hero.ssoHint')}>
      <Flex align="center" gap={4}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {t('users.hero.via')}
        </Text>
        <SourceTag ssoId={user?.sso_id ?? profile.sso_id} />
      </Flex>
    </Tooltip>
  ].filter(Boolean) as ReactNode[]

  const renderDividedRow = (items: ReactNode[]) =>
    items.flatMap((item, index) =>
      index < items.length - 1
        ? [
            item,
            <Divider
              key={`divider-${index}`}
              type="vertical"
              style={{ margin: 0 }}
            />
          ]
        : [item]
    )

  // Meta row: email · phone · reports-to · joined, dot-joined. Last sign-in is
  // omitted here — this is always the current user's own profile, where it's
  // just "moments ago" and adds no signal.
  const topMetaItems: ReactNode[] = [
    <Tooltip key="user-id" title={t('users.hero.userId')}>
      <Text
        type="secondary"
        style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
      >
        {`# ${user?.id ?? profile.id}`}
      </Text>
    </Tooltip>,
    <Flex key="email" align="center" gap={5}>
      <Mail size={12} />
      <Text
        copyable={{ tooltips: false }}
        style={{ fontSize: 12 }}
        ellipsis={{ tooltip: email }}
      >
        {email}
      </Text>
    </Flex>,
    formattedPhone ? (
      <Flex key="phone" align="center" gap={5}>
        <Phone size={12} />
        <Text
          type="secondary"
          style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
        >
          {formattedPhone}
        </Text>
      </Flex>
    ) : null,
    managerName ? (
      <Flex key="manager" align="center" gap={5}>
        <UserRound size={12} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('users.detail.workspaceAccess.headUser')}: {managerName}
        </Text>
      </Flex>
    ) : null,
    record?.joined_date ? (
      <Flex key="joined" align="center" gap={5}>
        <CalendarDays size={12} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('users.detail.workspaceAccess.joined')}:{' '}
        </Text>
        <DateTimeCell
          value={record.joined_date}
          variant="date"
          style={{ fontSize: 12 }}
        />
      </Flex>
    ) : null
  ].filter(Boolean) as ReactNode[]

  const renderDottedRow = (items: ReactNode[]) =>
    items.flatMap((item, index) =>
      index < items.length - 1
        ? [
            item,
            <Text
              key={`dot-${index}`}
              type="secondary"
              style={{ fontSize: 12 }}
            >
              ·
            </Text>
          ]
        : [item]
    )

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <Flex
        justify="space-between"
        align="flex-start"
        gap={16}
        style={{ padding: 12 }}
      >
        <Flex align="flex-start" gap={14} style={{ minWidth: 0 }}>
          {canUploadAvatar ? (
            <UploadAvatar
              user={profile}
              userId={String(profile.id)}
              trigger={(openAvatarModal) => (
                <button
                  type="button"
                  onClick={openAvatarModal}
                  aria-label={t('account.avatar.change')}
                  style={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    flex: '0 0 auto',
                    lineHeight: 0,
                    padding: 0
                  }}
                >
                  <AvatarWithInitials
                    id={profile.id}
                    name={displayName}
                    size={48}
                    initialsFontSize={18}
                    src={record?.user.photo?.file ?? photoSrc}
                  />
                </button>
              )}
            />
          ) : (
            <AvatarWithInitials
              id={profile.id}
              name={displayName}
              size={48}
              initialsFontSize={18}
              src={record?.user.photo?.file ?? photoSrc}
            />
          )}
          <Flex vertical gap={6} style={{ minWidth: 0 }}>
            <Flex align="center" gap={8} wrap>
              <Title
                level={3}
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: 1.2
                }}
              >
                {displayName}
              </Title>
              {renderDividedRow(headingMetaItems)}
            </Flex>
            <Flex
              align="center"
              gap={8}
              wrap
              style={{ color: 'var(--ant-color-text-tertiary)', fontSize: 12 }}
            >
              {renderDottedRow(topMetaItems)}
            </Flex>
          </Flex>
        </Flex>
        {onEdit ? (
          <Button
            type="primary"
            size="small"
            icon={<Pencil size={14} />}
            onClick={onEdit}
          >
            {t('users.actions.edit')}
          </Button>
        ) : null}
      </Flex>
    </Card>
  )
}
